import type { Commonv1Value } from '@/generated/hooks/grimoire/models';
import { createStore } from './config';

/**
 * Data structure for storing automation input values and validation state
 */
interface AutomationInputsData {
  /** Map of input field names to their Commonv1Value values */
  values: Record<string, Commonv1Value>;
  /** Optional map of input field names to validation error messages */
  errors?: Record<string, string>;
}

/**
 * Zustand store state for managing automation input values across automations
 *
 * This store persists input data in sessionStorage, allowing users to maintain
 * input values during their session even when navigating between pages.
 */
interface AutomationInputsState {
  // State: Map of automationId to input data
  /** Map of automation IDs to their input data */
  automationInputs: Record<string, AutomationInputsData>;

  // Actions
  /**
   * Sets or updates the input values and errors for a specific automation
   *
   * @param automationId - The unique identifier of the automation
   * @param values - Map of input field names to their values
   * @param errors - Optional map of input field names to error messages
   */
  setAutomationInputs: (
    automationId: string,
    values: Record<string, Commonv1Value>,
    errors?: Record<string, string>,
  ) => void;

  /**
   * Retrieves the input data for a specific automation
   *
   * @param automationId - The unique identifier of the automation
   * @returns The automation input data, or undefined if not found
   */
  getAutomationInputs: (
    automationId: string,
  ) => AutomationInputsData | undefined;

  /**
   * Removes all input data for a specific automation
   *
   * @param automationId - The unique identifier of the automation to clear
   */
  clearAutomationInputs: (automationId: string) => void;

  /**
   * Clears all input data for all automations
   */
  clearAllInputs: () => void;
}

export const useAutomationInputsStore = createStore<AutomationInputsState>(
  'automation-inputs-store',
  (_, get, api) => ({
    // Initial state
    automationInputs: {},

    // Actions
    setAutomationInputs: (
      automationId: string,
      values: Record<string, Commonv1Value>,
      errors?: Record<string, string>,
    ) => {
      const currentInputs = get().automationInputs;
      api.setState(
        {
          automationInputs: {
            ...currentInputs,
            [automationId]: {
              values,
              errors,
            },
          },
        },
        false,
        {
          type: 'automationInputs/setAutomationInputs',
          automationId,
          valuesCount: Object.keys(values).length,
        },
      );
    },

    getAutomationInputs: (
      automationId: string,
    ): AutomationInputsData | undefined => {
      return get().automationInputs[automationId];
    },

    clearAutomationInputs: (automationId: string) => {
      const currentInputs = { ...get().automationInputs };
      delete currentInputs[automationId];
      api.setState({ automationInputs: currentInputs }, false, {
        type: 'automationInputs/clearAutomationInputs',
        automationId,
      });
    },

    clearAllInputs: () => {
      api.setState(
        { automationInputs: {} },
        false,
        'automationInputs/clearAllInputs',
      );
    },
  }),
);

// Selectors
/**
 * Selector to get all automation inputs from the store
 *
 * @param state - The automation inputs store state
 * @returns Map of automation IDs to their input data
 */
export const selectAutomationInputs = (state: AutomationInputsState) =>
  state.automationInputs;

/**
 * Selector to get the setAutomationInputs action
 *
 * @param state - The automation inputs store state
 * @returns The setAutomationInputs action function
 */
export const selectSetAutomationInputs = (state: AutomationInputsState) =>
  state.setAutomationInputs;

/**
 * Selector to get the getAutomationInputs action
 *
 * @param state - The automation inputs store state
 * @returns The getAutomationInputs action function
 */
export const selectGetAutomationInputs = (state: AutomationInputsState) =>
  state.getAutomationInputs;

/**
 * Selector to get the clearAutomationInputs action
 *
 * @param state - The automation inputs store state
 * @returns The clearAutomationInputs action function
 */
export const selectClearAutomationInputs = (state: AutomationInputsState) =>
  state.clearAutomationInputs;

/**
 * Selector to get the clearAllInputs action
 *
 * @param state - The automation inputs store state
 * @returns The clearAllInputs action function
 */
export const selectClearAllInputs = (state: AutomationInputsState) =>
  state.clearAllInputs;
